/**
 * MonthGrid — prezentacyjna siatka kalendarza miesięcznego (6 × 7), bez zależności
 * od kształtu danych. Wpisy dostaje już rozwiązane (`CalendarEvent[]`); nawigacja,
 * legenda i klik są sterowane przez propsy. Współdzielona przez `CalendarView`
 * (moduły custom) i `CalendarBoard` (silnik list). Bez zewnętrznych bibliotek.
 */

import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';

import {
  buildCalendarGrid,
  type CalendarEvent,
  getReadableTextColor,
  groupEventsByDate,
  MONTHS_PL,
  NO_DATE_COLOR,
  toLocalDateStr,
  WEEK_DAYS_PL,
} from './calendar-utils';

interface MonthGridProps {
  year: number;
  month: number;
  events: CalendarEvent[];
  /** Wpisy bez daty startu — pasek „Brak daty" pod siatką. */
  noDateEvents?: CalendarEvent[];
  /** Legenda kolorów (np. opcje pola statusu). */
  legend?: Array<{ label: string; color: string }>;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onEventClick: (eventId: string) => void;
  onDayClick?: (date: Date) => void;
  /** Dodatkowy element w pasku narzędzi (np. wybór pola daty w module custom). */
  toolbarExtra?: React.ReactNode;
  /**
   * Tłumi komunikat pustego miesiąca na czas dociągania danych. W trybie serwerowym z
   * `keepPreviousData` zmiana miesiąca zostawia na moment wiersze POPRZEDNIEGO miesiąca —
   * przefiltrowane do nowego miesiąca dają 0 wpisów, co bez tego zapala fałszywy baner „brak".
   */
  suppressEmptyMessage?: boolean;
  /**
   * Komunikat nad siatką (pod paskiem narzędzi i legendą) — np. „pokazano część wpisów miesiąca"
   * z `CalendarBoard`. Brak = nic. Siatka zostaje widoczna.
   */
  notice?: React.ReactNode;
  /**
   * Gdy `true`, siatka wypełnia wysokość rodzica: rzędy tygodni rozciągają się równomiernie
   * (`auto-rows-fr`) zamiast stałego `min-h`. Rozciągają się TYLKO rzędy — zawartość dnia bez zmian:
   * nadmiar wpisów nadal ucinany do 3 + „+N" (komórka ma `overflow-hidden`, nie scrolluje). Gdy
   * rodzic jest niższy niż ~6 rzędów po `min-h`, wrapper siatki scrolluje w pionie (komórki trzymają
   * minimalną wysokość, dolny tydzień nie znika bez śladu). Wymaga rodzica z ograniczoną wysokością
   * (np. `ConfigurableTable fillHeight`). Domyślnie `false` — zachowanie wsteczne (stała wysokość
   * komórek), bez zmian dla kalendarza modułów własnych.
   */
  fillHeight?: boolean;
  testId?: string;
}

interface DayCellProps {
  date: Date | null;
  events: CalendarEvent[];
  /** Kolor tekstu wpisu per kolor tła — policzony RAZ na unikalny kolor w `MonthGrid`. */
  textColorByBackground: ReadonlyMap<string, string>;
  isToday: boolean;
  isCurrentMonth: boolean;
  onEventClick: (eventId: string) => void;
  onDayClick?: (date: Date) => void;
}

function DayCell({
  date,
  events,
  textColorByBackground,
  isToday,
  isCurrentMonth,
  onEventClick,
  onDayClick,
}: DayCellProps) {
  if (!date) {
    return <div className="min-h-[80px] border border-transparent bg-bg-secondary/30" />;
  }

  return (
    <div
      className={`relative min-h-[80px] cursor-pointer border border-border-primary p-1 transition-colors hover:bg-bg-secondary/50 ${
        isCurrentMonth ? 'bg-bg-primary' : 'bg-bg-secondary/30'
      }`}
      onClick={() => onDayClick?.(date)}
      data-testid={`calendar-day-${toLocalDateStr(date)}`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ${
          isToday
            ? 'bg-accent-500 text-white'
            : isCurrentMonth
              ? 'text-text-primary'
              : 'text-text-muted'
        }`}
      >
        {date.getDate()}
      </span>

      <div className="mt-0.5 space-y-0.5 overflow-hidden">
        {events.slice(0, 3).map((ev) => (
          <Button
            key={ev.id}
            variant="ghost"
            size="sm"
            className="block w-full truncate rounded px-1 py-0.5 text-left text-xs font-normal transition-opacity hover:opacity-80"
            // Kolor tekstu wg jasności tła (ciemny na jasnym, biały na ciemnym) — inline, bo tło
            // też jest inline i może być tokenem palety rozwiązywanym w czasie działania.
            style={{ backgroundColor: ev.color, color: textColorByBackground.get(ev.color) }}
            onClick={(e) => {
              e.stopPropagation();
              onEventClick(ev.id);
            }}
            title={ev.label}
            data-testid={`calendar-record-${ev.id}`}
          >
            {ev.label}
          </Button>
        ))}
        {events.length > 3 && (
          <span className="block pl-1 text-xs text-text-muted">+{events.length - 3}</span>
        )}
      </div>
    </div>
  );
}

export function MonthGrid({
  year,
  month,
  events,
  noDateEvents = [],
  legend,
  onPrevMonth,
  onNextMonth,
  onToday,
  onEventClick,
  onDayClick,
  toolbarExtra,
  suppressEmptyMessage = false,
  notice,
  fillHeight = false,
  testId = 'calendar-view',
}: MonthGridProps) {
  const { t } = useTranslation();

  const grid = useMemo(() => buildCalendarGrid(year, month), [year, month]);
  const eventsByDate = useMemo(() => groupEventsByDate(events, year, month), [events, year, month]);
  // Kolor tekstu liczymy raz na UNIKALNY kolor tła, nie per chip: dla tokenu palety to odczyt
  // `getComputedStyle` z korzenia dokumentu, a miesiąc może mieć dziesiątki wpisów w kilku kolorach.
  const textColorByBackground = useMemo(() => {
    const byBackground = new Map<string, string>();
    for (const ev of events) {
      if (!byBackground.has(ev.color)) byBackground.set(ev.color, getReadableTextColor(ev.color));
    }
    return byBackground;
  }, [events]);
  // Pasek „Brak daty" ma jeden stały kolor tła — tekst liczony raz, poza pętlą po wpisach
  // (i tylko gdy pasek w ogóle jest renderowany); `useMemo` po fladze obecności paska, żeby nie
  // czytać `getComputedStyle` przy każdym renderze.
  const hasNoDate = noDateEvents.length > 0;
  const noDateTextColor = useMemo(
    () => (hasNoDate ? getReadableTextColor(NO_DATE_COLOR) : undefined),
    [hasNoDate],
  );
  const todayStr = toLocalDateStr(new Date());
  const isMonthEmpty = eventsByDate.size === 0 && noDateEvents.length === 0;

  return (
    <div
      className={fillHeight ? 'flex h-full min-h-0 flex-col gap-3' : 'space-y-3'}
      data-testid={testId}
    >
      {/* Toolbar */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('tenant.modules.calendar.prevMonth')}
            onClick={onPrevMonth}
          >
            <ChevronLeft />
          </IconButton>
          <span className="min-w-[140px] text-center font-semibold text-text-primary">
            {MONTHS_PL[month]} {year}
          </span>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('tenant.modules.calendar.nextMonth')}
            onClick={onNextMonth}
          >
            <ChevronRight />
          </IconButton>
          <Button variant="secondary" size="sm" className="text-xs" onClick={onToday}>
            {t('tenant.modules.calendar.today')}
          </Button>
        </div>

        {toolbarExtra}
      </div>

      {/* Legenda */}
      {legend && legend.length > 0 && (
        <div className="flex flex-wrap items-center gap-3" data-testid="calendar-legend">
          {legend.map((item) => (
            <span
              key={item.label}
              className="flex items-center gap-1.5 text-xs text-text-secondary"
            >
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
      )}

      {/* Pusty miesiąc — czytelny komunikat nad siatką (siatka zostaje widoczna). Tłumiony na
          czas dociągania danych, żeby nie migać fałszywym „brak" przy zmianie miesiąca. */}
      {isMonthEmpty && !suppressEmptyMessage && (
        <div
          className="shrink-0 rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-center text-sm text-text-secondary"
          data-testid="calendar-empty-month"
        >
          {t('tenant.modules.calendar.emptyMonth')}
        </div>
      )}

      {notice}

      {/* Grid */}
      <div
        className={`rounded-lg border border-border-primary ${
          fillHeight ? 'flex min-h-0 flex-1 flex-col overflow-auto' : 'overflow-hidden'
        }`}
      >
        <div className="grid shrink-0 grid-cols-7 border-b border-border-primary bg-bg-secondary">
          {WEEK_DAYS_PL.map((d) => (
            <div key={d} className="px-2 py-1.5 text-center text-xs font-medium text-text-muted">
              {d}
            </div>
          ))}
        </div>

        <div className={`grid grid-cols-7 ${fillHeight ? 'min-h-0 flex-1 auto-rows-fr' : ''}`}>
          {grid.map((date, i) => {
            const dateStr = date ? toLocalDateStr(date) : null;
            const dayEvents = dateStr ? (eventsByDate.get(dateStr) ?? []) : [];
            return (
              <DayCell
                key={i}
                date={date}
                events={dayEvents}
                textColorByBackground={textColorByBackground}
                isToday={dateStr === todayStr}
                isCurrentMonth={!!date && date.getMonth() === month}
                onEventClick={onEventClick}
                onDayClick={onDayClick}
              />
            );
          })}
        </div>
      </div>

      {/* Rekordy bez daty */}
      {hasNoDate && (
        <div className="rounded-lg border border-border-primary bg-bg-secondary p-3">
          <p className="mb-2 text-xs font-medium text-text-muted">
            {t('tenant.modules.calendar.noDate')} ({noDateEvents.length})
          </p>
          <div className="flex flex-wrap gap-1">
            {noDateEvents.slice(0, 20).map((ev) => (
              <Button
                key={ev.id}
                variant="ghost"
                size="sm"
                className="rounded px-2 py-0.5 text-xs font-normal hover:opacity-80"
                style={{ backgroundColor: NO_DATE_COLOR, color: noDateTextColor }}
                onClick={() => onEventClick(ev.id)}
              >
                {ev.label}
              </Button>
            ))}
            {noDateEvents.length > 20 && (
              <span className="text-xs text-text-muted">+{noDateEvents.length - 20}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
